<template>
  <div class="ncrm-smtp-workspace">
    <div class="d-flex flex-wrap align-center ga-3 mb-4">
      <h2 class="ncrm-smtp-workspace__title">
        {{ $t("ncrm.integrations.smtp.title") }}
      </h2>
      <span class="ncrm-smtp-workspace__subtitle">
        {{ $t("ncrm.integrations.smtp.subtitle") }}
      </span>
      <v-spacer />
      <v-btn color="primary" @click="openCreateDialog">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.integrations.smtp.addAccount") }}
      </v-btn>
    </div>

    <NcrmCompanySmtpAccountsTable
      :accounts="accounts"
      :table-loading="tableLoading"
      :testing-id="testingId"
      :selected-account-id="selectedAccountId"
      @select="handleAccountSelection"
      @test="handleTest"
      @send-test="openSendTestDialog"
      @edit="openEditDialog"
      @delete="openDeleteDialog"
    />

    <NcrmCompanySmtpFormDialog
      :dialog="formDialog"
      :provider-options="providerOptions"
      :status-options="statusOptions"
      :auth-options="authOptions"
      @close="closeFormDialog"
      @save="submitForm"
      @provider-change="handleProviderChange"
    />

    <NcrmCompanySmtpSendTestDialog
      :dialog="testEmailDialog"
      @close="closeSendTestDialog"
      @send="submitSendTest"
    />

    <NcrmCompanySmtpDeleteDialog
      :dialog="deleteDialog"
      @close="closeDeleteDialog"
      @confirm="confirmDelete"
    />

    <v-snackbar v-model="snackbar.show" :timeout="3500" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import {
  COMPANY_SMTP_PROVIDER_PRESETS,
  applyCompanySmtpProviderPreset,
  buildCompanySmtpPayload,
  createCompanySmtpAccount,
  createCompanySmtpDraft,
  deleteCompanySmtpAccount,
  fetchCompanySmtpAccounts,
  normalizeCompanySmtpAccount,
  sendCompanySmtpTestEmail,
  testCompanySmtpAccount,
  updateCompanySmtpAccount,
} from "@/Utile/companyEmailSmtp.mjs";
import NcrmCompanySmtpAccountsTable from "@/components/ncrm/integrations/NcrmCompanySmtpAccountsTable.vue";
import NcrmCompanySmtpDeleteDialog from "@/components/ncrm/integrations/NcrmCompanySmtpDeleteDialog.vue";
import NcrmCompanySmtpFormDialog from "@/components/ncrm/integrations/NcrmCompanySmtpFormDialog.vue";
import NcrmCompanySmtpSendTestDialog from "@/components/ncrm/integrations/NcrmCompanySmtpSendTestDialog.vue";

export default {
  name: "NcrmCompanySmtpWorkspace",
  components: {
    NcrmCompanySmtpAccountsTable,
    NcrmCompanySmtpDeleteDialog,
    NcrmCompanySmtpFormDialog,
    NcrmCompanySmtpSendTestDialog,
  },
  data() {
    return {
      accounts: [],
      tableLoading: false,
      testingId: null,
      selectedAccountId: null,
      formDialog: {
        show: false,
        mode: "create",
        editPassword: false,
        saving: false,
        error: "",
        existingHasSecret: false,
        data: createCompanySmtpDraft(),
      },
      testEmailDialog: {
        show: false,
        account: null,
        to: "",
        sending: false,
        error: "",
      },
      deleteDialog: {
        show: false,
        account: null,
        confirmation: "",
        deleting: false,
        error: "",
      },
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    providerOptions() {
      return COMPANY_SMTP_PROVIDER_PRESETS.map((preset) => ({
        title: this.$t(preset.labelKey),
        value: preset.value,
      }));
    },
    statusOptions() {
      return [
        { title: this.$t("ncrm.integrations.smtp.status.active"), value: "active" },
        { title: this.$t("ncrm.integrations.smtp.status.disabled"), value: "disabled" },
      ];
    },
    authOptions() {
      return [
        { title: this.$t("ncrm.integrations.smtp.auth.password"), value: "password" },
        { title: this.$t("ncrm.integrations.smtp.auth.none"), value: "none" },
      ];
    },
  },
  mounted() {
    this.fetchAccounts();
  },
  methods: {
    extractError(error, fallbackKey) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        this.$t(fallbackKey)
      );
    },
    showSnackbar(message, color = "info") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    async fetchAccounts() {
      this.tableLoading = true;
      try {
        const { data } = await fetchCompanySmtpAccounts();
        this.accounts = Array.isArray(data?.accounts)
          ? data.accounts.map(normalizeCompanySmtpAccount)
          : [];
        if (this.accounts.length === 1) {
          this.selectedAccountId = this.accounts[0].id;
        } else if (
          this.selectedAccountId &&
          !this.accounts.some((account) => account.id === this.selectedAccountId)
        ) {
          this.selectedAccountId = null;
        }
      } catch (error) {
        this.showSnackbar(
          this.extractError(error, "ncrm.integrations.smtp.snackbar.loadError"),
          "error"
        );
      } finally {
        this.tableLoading = false;
      }
    },
    handleAccountSelection(item) {
      if (!item?.id) return;
      this.selectedAccountId = item.id === this.selectedAccountId ? null : item.id;
    },
    openCreateDialog() {
      this.formDialog = {
        show: true,
        mode: "create",
        editPassword: false,
        saving: false,
        error: "",
        existingHasSecret: false,
        data: createCompanySmtpDraft(),
      };
    },
    openEditDialog(account) {
      this.formDialog = {
        show: true,
        mode: "edit",
        editPassword: false,
        saving: false,
        error: "",
        existingHasSecret: Boolean(account.secret_key_version),
        data: {
          id: account.id,
          name: account.name || "",
          description: account.description || "",
          providerKey: account.provider_key || "generic",
          isDefault: Boolean(account.is_default),
          status: account.status || "active",
          host: account.host || "",
          port: Number(account.port || 587),
          secure: Boolean(account.secure),
          requireTLS: Boolean(account.require_tls),
          authType: account.auth_type || "password",
          username: account.username || "",
          password: "",
          fromEmail: account.from_email || "",
          fromName: account.from_name || "",
          replyToEmail: account.reply_to_email || "",
        },
      };
    },
    closeFormDialog() {
      this.formDialog.show = false;
    },
    handleProviderChange(providerKey) {
      this.formDialog.data = applyCompanySmtpProviderPreset(this.formDialog.data, providerKey);
    },
    validateForm() {
      const data = this.formDialog.data;
      const requiredValues = [data.name, data.host, data.port, data.fromEmail];
      if (requiredValues.some((value) => !String(value || "").trim())) {
        this.formDialog.error = this.$t("ncrm.integrations.smtp.dialog.requiredField");
        return false;
      }
      const port = Number(data.port);
      if (!Number.isInteger(port) || port < 1 || port > 65535) {
        this.formDialog.error = this.$t("ncrm.integrations.smtp.dialog.invalidPort");
        return false;
      }
      if (data.authType === "password" && !String(data.username || "").trim()) {
        this.formDialog.error = this.$t("ncrm.integrations.smtp.dialog.requiredField");
        return false;
      }
      const needsPassword =
        data.authType === "password" &&
        (this.formDialog.mode === "create" ||
          this.formDialog.editPassword ||
          !this.formDialog.existingHasSecret);
      if (needsPassword && !String(data.password || "").trim()) {
        this.formDialog.error = this.$t("ncrm.integrations.smtp.dialog.passwordRequired");
        return false;
      }
      return true;
    },
    async submitForm() {
      this.formDialog.error = "";
      if (!this.validateForm()) return;
      this.formDialog.saving = true;
      try {
        const includePassword =
          this.formDialog.data.authType === "password" &&
          (this.formDialog.mode === "create" || this.formDialog.editPassword);
        const payload = buildCompanySmtpPayload(this.formDialog.data, { includePassword });
        if (this.formDialog.mode === "create") {
          await createCompanySmtpAccount(payload);
          this.showSnackbar(this.$t("ncrm.integrations.smtp.snackbar.created"), "success");
        } else {
          await updateCompanySmtpAccount(this.formDialog.data.id, payload);
          this.showSnackbar(this.$t("ncrm.integrations.smtp.snackbar.updated"), "success");
        }
        this.formDialog.show = false;
        await this.fetchAccounts();
      } catch (error) {
        this.formDialog.error = this.extractError(
          error,
          "ncrm.integrations.smtp.snackbar.saveError"
        );
      } finally {
        this.formDialog.saving = false;
      }
    },
    openSendTestDialog(account) {
      this.testEmailDialog = {
        show: true,
        account,
        to: "",
        sending: false,
        error: "",
      };
    },
    closeSendTestDialog() {
      this.testEmailDialog.show = false;
    },
    async submitSendTest() {
      this.testEmailDialog.error = "";
      if (!String(this.testEmailDialog.to || "").trim()) {
        this.testEmailDialog.error = this.$t("ncrm.integrations.smtp.dialog.requiredField");
        return;
      }
      this.testEmailDialog.sending = true;
      try {
        const { data } = await sendCompanySmtpTestEmail(
          this.testEmailDialog.account.id,
          this.testEmailDialog.to.trim()
        );
        this.showSnackbar(
          this.$t("ncrm.integrations.smtp.snackbar.sendTestOk", {
            messageId: data?.messageId || "-",
          }),
          "success"
        );
        this.testEmailDialog.show = false;
      } catch (error) {
        this.testEmailDialog.error = this.extractError(
          error,
          "ncrm.integrations.smtp.snackbar.sendTestError"
        );
      } finally {
        this.testEmailDialog.sending = false;
      }
    },
    openDeleteDialog(account) {
      this.deleteDialog = {
        show: true,
        account,
        confirmation: "",
        deleting: false,
        error: "",
      };
    },
    closeDeleteDialog() {
      this.deleteDialog.show = false;
    },
    async confirmDelete() {
      if (this.deleteDialog.confirmation !== "DELETE") return;
      this.deleteDialog.deleting = true;
      this.deleteDialog.error = "";
      try {
        await deleteCompanySmtpAccount(this.deleteDialog.account.id);
        this.showSnackbar(this.$t("ncrm.integrations.smtp.snackbar.deleted"), "success");
        this.deleteDialog.show = false;
        await this.fetchAccounts();
      } catch (error) {
        this.deleteDialog.error = this.extractError(
          error,
          "ncrm.integrations.smtp.snackbar.deleteError"
        );
      } finally {
        this.deleteDialog.deleting = false;
      }
    },
    async handleTest(account) {
      this.testingId = account.id;
      try {
        const { data } = await testCompanySmtpAccount(account.id);
        if (data?.ok) {
          this.showSnackbar(this.$t("ncrm.integrations.smtp.snackbar.testOk"), "success");
        } else {
          this.showSnackbar(
            data?.error || this.$t("ncrm.integrations.smtp.snackbar.testError"),
            "error"
          );
        }
        await this.fetchAccounts();
      } catch (error) {
        this.showSnackbar(
          this.extractError(error, "ncrm.integrations.smtp.snackbar.testError"),
          "error"
        );
      } finally {
        this.testingId = null;
      }
    },
  },
};
</script>

<style scoped>
.ncrm-smtp-workspace__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-strong);
}

.ncrm-smtp-workspace__subtitle {
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.7;
  padding-left: 8px;
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.18);
}

</style>
